import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  CARD_NETWORKS,
  CardPaymentMethods,
  cardProviders,
  mainCardTypes,
  orderCardFlowScreens,
} from '../../../constants';
import { Button, Typography } from '../../../../../components';
import ModalBodyLayout from '../partials/MainBodyLayout';

const CardPromo = () => {
  const { t: translate } = useTranslation();
  const [nextScreen, setNextScreen] = useState(null);

  const { state, openScreen, updatePaymentInfo } = useContext(OrderCardContext);

  const orderCardFreeLimitBalance = state?.cardOrderSettings?.server?.promo_amount_free_card;
  const orderCardFreeLimitBalanceCurrency =
    state?.cardOrderSettings?.server?.promo_amount_free_card_currency;

  const userRegionSettings = state?.regionSettings;
  const cardOptions = state?.cardOptions;
  const cardType = state?.cardType;
  const mainCardType = state?.mainCardType;
  const cardProvider = state?.cardProvider;
  const { isEligible, isWaitingList } = state?.freeCardEligible;
  const orderableCardTypes = state?.orderableCardTypes;
  const cardNetwork = state?.cardNetwork;
  const cardProvidersWithTypesObj = state?.cardProvidersWithTypesObj;

  const eligibleNote = useMemo(() => {
    if (isEligible) {
      if (isWaitingList) {
        return translate('card_order_promo_eligible_free_card_waiting_list_user');
      }
      return translate('card_order_promo_eligible_free_card', {
        amount: orderCardFreeLimitBalance,
        currency: orderCardFreeLimitBalanceCurrency,
      });
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCardFreeLimitBalance, orderCardFreeLimitBalanceCurrency, isEligible, isWaitingList]);

  const CardImage = useMemo(() => {
    if (
      cardProvider === cardProviders.GALILEO &&
      mainCardType === mainCardTypes.VIRTUAL &&
      cardProvidersWithTypesObj &&
      cardProvider in cardProvidersWithTypesObj &&
      cardProvidersWithTypesObj[cardProvider][mainCardType.toUpperCase()] === CARD_NETWORKS.VISA
    ) {
      return (
        <img
          className="w-80 pb-5"
          src="./assets/images/cl-card-galileo-visa-virtual.png"
          alt="promo"
        />
      );
    }

    if (
      cardProvider === cardProviders.GALILEO &&
      cardProvidersWithTypesObj &&
      cardProvider in cardProvidersWithTypesObj &&
      cardProvidersWithTypesObj[cardProvider][mainCardType.toUpperCase()] === CARD_NETWORKS.VISA
    ) {
      return (
        <img className="w-80 pb-5" src="./assets/images/cl-card-galileo-visa.png" alt="promo" />
      );
    }

    if (
      cardProvider === cardProviders.GALILEO &&
      mainCardType === mainCardTypes.VIRTUAL &&
      cardProvidersWithTypesObj[cardProvider][mainCardType.toUpperCase()] ===
        CARD_NETWORKS.MASTERCARD
    ) {
      return (
        <img
          className="w-80 pb-5"
          src="./assets/images/cl-card-galileo-mastercard-virtual.png"
          alt="promo"
        />
      );
    }

    if (
      cardProvider === cardProviders.GALILEO &&
      cardProvidersWithTypesObj[cardProvider][mainCardType.toUpperCase()] ===
        CARD_NETWORKS.MASTERCARD
    ) {
      return (
        <img
          className="w-80 pb-5"
          src="./assets/images/cl-card-galileo-mastercard.png"
          alt="promo"
        />
      );
    }

    if (
      cardType === cardProviders.MONAVATE ||
      (cardProvider === cardProviders.MONAVATE &&
        userRegionSettings?.default_card_type?.toLowerCase() === mainCardTypes.VIRTUAL)
    ) {
      return <img className="mw-100 pb-5" src="./assets/images/cl-card-monavate.png" alt="promo" />;
    }
    if (cardProvider === cardProviders.GALILEO && cardNetwork === CARD_NETWORKS.MASTERCARD) {
      return <img className="mw-100 pb-5" src="./assets/images/cl-card-monavate.png" alt="promo" />;
    }
    return <img className="mw-100 pb-5" src="./assets/images/order-card-visa.png" alt="promo" />;
  }, [
    cardType,
    userRegionSettings,
    cardProvider,
    cardNetwork,
    mainCardType,
    cardProvidersWithTypesObj,
  ]);

  useEffect(() => {
    updatePaymentInfo('method', CardPaymentMethods.CRYPTO_OR_STABLE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Need to be updated if we ganna allow choosing from multiple card providers
    if (mainCardType === mainCardTypes.VIRTUAL) {
      setNextScreen(orderCardFlowScreens.CONFIRM_VIRTUAL_DETAILS);
    } else if (cardProvider in cardOptions) {
      const availableCardNetworks = cardOptions[cardProvider].reduce((acc, curr) => {
        if (curr.type === mainCardTypes.PHYSICAL.toUpperCase()) {
          return [...acc, curr.name];
        }
        return acc;
      }, []);
      if (availableCardNetworks.length === 1) {
        setNextScreen(orderCardFlowScreens.CONFIRM_DETAILS);
      } else if (availableCardNetworks.length > 1) {
        setNextScreen(orderCardFlowScreens.CARD_TYPE);
      }
    }
  }, [cardOptions, cardProvider, mainCardType]);

  const renderFooter = () =>
    isEligible ? (
      <div className="btn-container">
        <Button
          variant="primary"
          className="w-100"
          onClick={() => {
            openScreen(nextScreen, {
              withBack: true,
            });
          }}>
          {translate('card_order_try_now_btn_label')}
        </Button>
      </div>
    ) : (
      <div className="btn-container">
        <Button
          variant="primarySimple"
          className="w-100"
          onClick={() => {
            openScreen(nextScreen, {
              withBack: true,
              isTopUp: false,
            });
          }}>
          {translate('card_order_pay_for_the_card')}
        </Button>
        <Button
          variant="primary"
          className="w-100"
          onClick={() => {
            openScreen(orderCardFlowScreens.SELECT_PAYMENT, { isTopUp: true });
          }}>
          {translate('card_order_top_up_an_account')}
        </Button>
      </div>
    );

  return (
    <ModalBodyLayout
      withBack={orderableCardTypes?.includes(mainCardTypes.VIRTUAL.toUpperCase()) && !isWaitingList}
      onPrevClick={() => openScreen(orderCardFlowScreens.SELECT_CARD_TYPE)}>
      <div className="card-order-promo-container">
        <div className="text-center pb-3">
          {CardImage}
          <Typography variant="largeSemi" className="h2 text-white mb-2">
            {isEligible
              ? translate('card_order_you_can_get_free_card')
              : translate('card_order_want_to_get_free_card')}
          </Typography>
          <Typography variant="base" className="pb-3 text-shadow">
            {isEligible
              ? eligibleNote
              : translate('card_order_promo_not_eligible_free_card', {
                  amount: orderCardFreeLimitBalance,
                  currency: orderCardFreeLimitBalanceCurrency,
                })}
          </Typography>
        </div>
      </div>
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default CardPromo;
